import { useId, useState } from 'react';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import type { ReactFlowNode } from '@/pages/Logiblocks/types';
import { formatVariableRef } from '@/pages/Logiblocks/variables/variableUtils';
import { keyedAnswerParts } from './keyedAnswerParts';
import styles from './QuestionAnswerSummary.module.css';

export type AnsweredQuestion = {
  id: string;
  prompt: string;
  labels: string[];
  templateChunks?: TextTemplateChunk[];
};

function answerText(answer: AnsweredQuestion, nodes: ReactFlowNode[]): string {
  if (!answer.templateChunks?.length) return answer.labels.join(', ');
  return answer.templateChunks
    .map((chunk) => {
      if (chunk.text !== undefined) return chunk.text;
      if (chunk.variable)
        return formatVariableRef(chunk.variable, nodes, 'short');
      if (chunk.user_mention) return `@${chunk.user_mention.label}`;
      return '';
    })
    .join('');
}

function AnswerValue({
  answer,
  nodes,
}: {
  answer: AnsweredQuestion;
  nodes: ReactFlowNode[];
}) {
  if (answer.templateChunks?.length) {
    return keyedAnswerParts(answer.templateChunks, JSON.stringify).map(
      ({ key, value: chunk }) => {
        if (chunk.text !== undefined)
          return <span key={key}>{chunk.text}</span>;
        if (chunk.variable) {
          return (
            <span
              key={key}
              className={styles.variableChip}
              title={formatVariableRef(chunk.variable, nodes)}
            >
              {formatVariableRef(chunk.variable, nodes, 'short')}
            </span>
          );
        }
        if (chunk.user_mention) {
          return (
            <span key={key} className={styles.userMention}>
              @{chunk.user_mention.label}
            </span>
          );
        }
        return null;
      }
    );
  }
  return keyedAnswerParts(answer.labels, (label) => label).map(
    ({ key, value: label }, index) => (
      <span key={key}>
        {index > 0 && ', '}
        <span>{label}</span>
      </span>
    )
  );
}

export function QuestionAnswerSummary({
  answers,
  nodes,
}: {
  answers: AnsweredQuestion[];
  nodes: ReactFlowNode[];
}) {
  const contentId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const preview = answers.slice(0, 2);
  const canExpand =
    answers.length > preview.length ||
    preview.some(
      (answer) => answer.prompt.length + answerText(answer, nodes).length > 240
    );
  const expanded = canExpand && isExpanded;
  if (!answers.length) return null;

  return (
    <section className={styles.card} aria-label='Answered questions'>
      <div
        id={contentId}
        className={canExpand && !expanded ? styles.preview : undefined}
      >
        {(expanded ? answers : preview).map((answer) => (
          <div className={styles.item} key={answer.id}>
            <p className={styles.line}>
              Q: <span>{answer.prompt}</span>
            </p>
            <p className={styles.line}>
              A: <AnswerValue answer={answer} nodes={nodes} />
            </p>
          </div>
        ))}
      </div>
      {canExpand && (
        <button
          type='button'
          className={styles.toggle}
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setIsExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </section>
  );
}
